import React from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

import { LocationListData } from 'op-interfaces/Locations';
import PhotoWidget from 'shared-components/components/UIFormComponents/PhotoWidget';
import { DropDownField } from 'shared-components/components/FormFields';

import PatientId from './PatientId';
import PatientDetailsChip from './PatientDetailsChip';
import {
  GET_LOCATION_PROFILE,
  UPDATE_CENTER_MUTATION,
  UPDATE_PATIENT_TAGS,
  GET_TAG_OPTIONS,
} from './PatientCardQueries';
import { DetailedCardProps } from './types';
import { isUs } from 'op-utils';

const StyledDropDownField = styled(DropDownField)`
  width: 100%;
  margin-top: 5px;
  text-overflow: ellipsis;
  padding-right: 32px !important;
  color: ${(props) => props.theme.palette.text.primary} !important;
`;

const DEFAULT_LOCATION_PROFILE = [{ id: '', alias: '' }];
const DEFAULT_TAGS_PROFILE = [{ id: '', name: '' }];

const PSOPatientCard = (props: DetailedCardProps): JSX.Element => {
  const { attributes, loading, moreDetailsClickHandler, showMoreDetails } = props;
  const theme = useTheme();
  const address = attributes.address?.formattedAddress ? attributes.address?.ukFormattedAddress : '-';

  const [saveHorizonCenterId] = useMutation(UPDATE_CENTER_MUTATION);
  const [updatePatientTagItems] = useMutation(UPDATE_PATIENT_TAGS);

  const updatePatientTags = (newTagId: any): any => {
    const updatedTags = [];
    if (newTagId !== 'None') {
      updatedTags.push(newTagId);
    }
    updatePatientTagItems({ variables: { patientId: attributes.patientId, updatedTags: updatedTags } });
  };

  // Map the locations to match the listdata type expected by the dropdown component
  const convertLocationsToListData = (locations: LocationListData[]): any => {
    return locations.map(({ alias: name, ...rest }) => ({ name, ...rest }));
  };

  const { data: locationProfileData, loading: loadingLocations } = useQuery(GET_LOCATION_PROFILE, {
    variables: { patientId: attributes.patientId },
    skip: !attributes.patientId,
  });

  const { data: tagData, loading: loadingTagOptions } = useQuery(GET_TAG_OPTIONS);

  return (
    <Box
      sx={{
        paddingY: 2,
        paddingRight: 2,
        width: '100%',
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'start',
        position: 'relative',
        gap: 2,
      }}>
      {!loading && (
        <>
          {/* Photo + name + more details */}
          <Stack direction="row" alignItems="start" sx={{ flex: '2 1 0', minWidth: 0 }}>
            <Box sx={{ mx: 2, display: 'flex', alignItems: 'center' }}>
              <PhotoWidget patientId={attributes.patientId} enableOverlay={true} />
            </Box>
            <Stack direction="row" flexWrap="wrap" rowGap={1} columnGap={2}>
              <PatientId attributes={attributes} />
              <Box sx={{ mt: 0.5 }}>
                <PatientDetailsChip showMoreDetails={showMoreDetails} onClick={moreDetailsClickHandler} />
              </Box>
            </Stack>
          </Stack>

          {/* Detail blocks */}
          <Stack direction="row" justifyContent="center" gap={4} sx={{ flex: '2 1 0' }}>
            <Stack direction="column" sx={{ width: '180px' }}>
              <Typography variant="body2" color="text.secondary">
                Address
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {address}
              </Typography>
            </Stack>

            <Stack direction="column" sx={{ width: '180px' }}>
              <Typography variant="body2" color="text.secondary">
                Date of birth
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {attributes.fullAge}
              </Typography>
              <Typography variant="body2">
                Mobile:{' '}
                <Typography component="span" variant="inherit" fontWeight="bold">
                  {attributes.mobile}
                </Typography>
              </Typography>
              <Typography variant="body2">
                Landline:{' '}
                <Typography component="span" variant="inherit" fontWeight="bold">
                  {attributes.landline}
                </Typography>
              </Typography>
            </Stack>
          </Stack>

          {/* Dropdowns */}
          <Stack direction="row" justifyContent="flex-end" gap={2} sx={{ flex: '2 1 0', pl: 2 }}>
            {!loadingLocations && (
              <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                <Typography variant="body2">Primary department</Typography>
                <StyledDropDownField
                  disabled={loadingLocations}
                  inputKey="pso-patient-primary-department"
                  inputName="pso-patient-primary-department"
                  placeholder="Please select"
                  defaultValue={attributes.horizonCenterId?.toString()}
                  options={convertLocationsToListData(
                    (locationProfileData && locationProfileData.locationsPsoSystem) || DEFAULT_LOCATION_PROFILE,
                  )}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                    saveHorizonCenterId({ variables: { id: attributes.patientId, horizonCenterId: e.target.value } });
                  }}
                />
              </Box>
            )}
            {!isUs() && !loadingTagOptions && (
              <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                <Typography variant="body2">Tag</Typography>
                <StyledDropDownField
                  disabled={loadingTagOptions}
                  inputKey="pso-patient-tag"
                  inputName="pso-patient-tag"
                  placeholder="None"
                  enabledPlaceholder={true}
                  defaultValue={attributes.tags[0]?.tag.id}
                  options={(tagData && tagData.tagOptions) || DEFAULT_TAGS_PROFILE}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updatePatientTags(e.target.value)}
                />
              </Box>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default PSOPatientCard;
